import { createFileRoute, useRouter } from "@tanstack/react-router";
import type { JSX } from "react";

import { graphql } from "@repo/graphql-types/gql";
import FemaleDoctorWithClipboard from "@repo/icons/default/FemaleDoctorWithClipboard";
import Left from "@repo/icons/default/Left";
import LocationMarker from "@repo/icons/default/LocationMarker";

import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { useGraphQL } from "@/hooks/use-graphql.ts";
import { MenuList, type MenuListItem } from "@/components/ui/layout/menu/menu-list";
import { isConsultorioVirtual } from "@/lib/helpers/unit";
import {
  EmptyStateButton,
  EmptyStateDescription,
  EmptyStateImage,
  EmptyStateRoot,
  EmptyStateTitle,
} from "@/components/ui/empty-state/empty-state";

const PreferredRoomsPageQuery = graphql(/* GraphQL */ `
  query PreferredRoomsPageQuery {
    unidades(
      where: {
        ativo: { _eq: true }
        usuariosAgendasUnidades: {
          ativo: { _eq: true }
          usuariosAgenda: { ativo: { _eq: true } }
        }
      }
      order_by: { nomeLimpo: asc }
    ) {
      codUnidade
      sigla
      nomeLimpo
    }
  }
`);

const PreferredRoomsPage = (): JSX.Element => {
  const queryResult = useGraphQL(PreferredRoomsPageQuery);
  const router = useRouter();

  const { data } = queryResult;
  const unidades = data?.unidades ?? [];

  const menuItems: MenuListItem[] = unidades.map((unidade) => {
    const unitName = unidade.nomeLimpo ?? "";
    const unitCode = unidade.sigla ?? "";
    const unitId = unidade.codUnidade;

    return {
      name: isConsultorioVirtual(unitId) ? unitName : `${unitName} (${unitCode})`,
      Icon: LocationMarker,
      route: "/settings/schedule-options/preferred-rooms/$unitId",
      params: { unitId: String(unidade.codUnidade) },
    };
  });

  const hasUnits = Boolean(unidades.length > 0);

  const handleAddConfigSchedule = (): void => {
    void router.navigate({ to: "/settings/schedule-options/schedules" });
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Salas preferenciais" />
      </HeaderRoot>
      <Page className="p-6">
        <AsyncDataWrapper {...queryResult}>
          {hasUnits ? (
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <h2 className="text-lg font-semibold text-neutral-600">
                  Selecione a unidade:
                </h2>
                <p className="text-sm text-neutral-500">
                  Indique suas salas preferidas em cada unidade com agenda aberta para
                  tentarmos alocá-lo nelas quando houver disponibilidade.
                </p>
              </div>

              <MenuList menuItems={menuItems} />
            </div>
          ) : (
            <EmptyStateRoot>
              <EmptyStateImage
                icon={FemaleDoctorWithClipboard}
                alt="Ainda não tem unidades com agenda aberta"
              />
              <EmptyStateTitle>
                Você ainda não possui agendas configuradas
              </EmptyStateTitle>
              <EmptyStateDescription>
                Antes de realizar o cadastro das salas preferenciais, é necessário
                configurar a sua agenda nas unidades de interesse.
              </EmptyStateDescription>
              <EmptyStateButton onClick={handleAddConfigSchedule} fullWidth>
                Configurar agenda
              </EmptyStateButton>
            </EmptyStateRoot>
          )}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/schedule-options/preferred-rooms/")({
  component: PreferredRoomsPage,
});
